import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import {
    Checkpoint2MeasureUnitType,
    Checkpoint2StatusType,
    CheckpointRangeType,
} from '@fi-shared/checklist/checklist-enum';
import {
    FiTooltipButtonIconColors,
    FiTooltipIconClassType,
} from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { ICheckpointCondition, ICheckpointRange } from '@fi-shared/checklist/checklist-v2';
import { isDefined } from '@fi-shared/utils';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { CheckpointRangeStatusOptions, CheckpointRangeTypesOptions } from '@fi-shared/checklist/checklist-consts';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { isNullOrUndefined } from 'util';
import { validateOnlyNumberKeyboardEvent } from '@fi-shared/utils/only-numbers-validator';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';

@Component({
    selector: 'fi-checkpoint-range-editor',
    templateUrl: './range-editor.component.html',
    styleUrls: ['./range-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-range-editor' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiCheckpointRangeEditorComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiCheckpointRangeEditorComponent,
        },
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'width-unset' },
        },
    ],
    standalone: false,
})
export class FiCheckpointRangeEditorComponent
    extends ControlValueAccessorValidatorBase<ICheckpointRange>
    implements OnInit, OnDestroy
{
    @Input() measureUnitType: Checkpoint2MeasureUnitType;
    @Input() allCheckpointRanges: ICheckpointRange[];
    @Input() checkpointConditions: ICheckpointCondition[] = [];
    @Input() checkpointRangeValidityUpdate$ = new Observable<void>();
    @Output() checkpointConditionsChange = new EventEmitter<ICheckpointCondition[]>();
    @Output() rangeRemoveClick = new EventEmitter();

    get checkpointRange(): ICheckpointRange {
        return this.ngModel;
    }

    readonly checkpointRangeTypesOptions: INameIdObj[] = CheckpointRangeTypesOptions;
    readonly checkpointRangeStatusOptions: INameIdObj[] = CheckpointRangeStatusOptions.filter(
        (item) => item.Id !== Checkpoint2StatusType.NotFilledOut,
    );
    readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;

    constructor(
        injector: Injector,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.checkpointRangeValidityUpdate$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.controlComponent.updateValueAndValidity());
    }

    getRangeSelectTriggerText(checkpointRange: ICheckpointRange): string {
        if (!isDefined(checkpointRange.CheckpointStatus)) {
            return '';
        }

        if (this.isOKRangeStatus(checkpointRange.CheckpointStatus))
            return CheckpointRangeStatusOptions.find((status) => status.Id === Checkpoint2StatusType.Ok).Name;

        return CheckpointRangeStatusOptions.find((status) => status.Id === Checkpoint2StatusType.NotOk).Name;
    }

    isOKRangeStatus(status: Checkpoint2StatusType): boolean {
        return status === Checkpoint2StatusType.Ok;
    }

    isCheckpointRangeTypeBetween(checkpointRange: ICheckpointRange): boolean {
        return checkpointRange.Type === CheckpointRangeType.Between;
    }

    isCheckpointRangeTypeLess(checkpointRange: ICheckpointRange): boolean {
        return checkpointRange.Type === CheckpointRangeType.Less;
    }

    onCheckpointRangeTypeChange(checkpointRange: ICheckpointRange) {
        checkpointRange.MaxValue = undefined;
        checkpointRange.MinValue = undefined;
        this.onCheckpointRangeSettingsChange();
    }

    onCheckpointRangeSettingsChange() {
        this.doPropagateChanges();
    }

    isRangeStatusOptionDisabled(statusType: Checkpoint2StatusType): boolean {
        return statusType === Checkpoint2StatusType.NotOk && this.allCheckpointRanges.length <= 1;
    }

    onRangeRemoveClick() {
        this.rangeRemoveClick.emit();
    }

    doValidate(c: AbstractControl): ValidationErrors {
        return {
            missingValues: this.isRangeMissingValues(),
            maxValueLessThenMinValue: this.isMinGreaterThanMaxRangeValue(),
            duplicateRange: this.isDuplicateRange(),
            rangeConflict: this.isRangeConflictsWithAnother(),
            noOkRanges: this.onlyNotOkRanges(),
        };
    }

    private isRangeMissingValues(): boolean {
        if (isNullOrUndefined(this.checkpointRange.Type) || isNullOrUndefined(this.checkpointRange.CheckpointStatus))
            return true;
        if (this.checkpointRange.Type === CheckpointRangeType.Between)
            return isNullOrUndefined(this.checkpointRange.MinValue) || isNullOrUndefined(this.checkpointRange.MaxValue);

        if (this.checkpointRange.Type === CheckpointRangeType.Less)
            return isNullOrUndefined(this.checkpointRange.MaxValue);

        if (this.checkpointRange.Type === CheckpointRangeType.Greater)
            return isNullOrUndefined(this.checkpointRange.MinValue);
    }

    private isDuplicateRange(): boolean {
        return (
            this.allCheckpointRanges.length > 1 &&
            !!this.allCheckpointRanges.find((targetRange: ICheckpointRange, targetIndex: number) => {
                if (targetIndex !== this.allCheckpointRanges.indexOf(this.checkpointRange))
                    return this.isDuplicateRangeWithSameCheckpointStatus(this.checkpointRange, targetRange);
            })
        );
    }

    private isRangeConflictsWithAnother(): boolean {
        if (this.allCheckpointRanges.length <= 1) return false;

        const sourceRangeIndex: number = this.allCheckpointRanges.indexOf(this.checkpointRange);

        if (!isDefined(this.checkpointRange.Type) || !isDefined(this.checkpointRange.CheckpointStatus)) return false;

        return !!this.allCheckpointRanges.find((targetRange: ICheckpointRange, targetRangeIndex: number) => {
            if (targetRangeIndex === sourceRangeIndex) return false;

            if (targetRange.CheckpointStatus === this.checkpointRange.CheckpointStatus) return false;

            if (!isDefined(targetRange.Type) || !isDefined(targetRange.CheckpointStatus)) return false;

            return (
                this.isOverlappingBetweenRangeType(this.checkpointRange, targetRange) ||
                this.isLessGreaterRangeOverlap(this.checkpointRange, targetRange)
            );
        });
    }

    private isOverlappingBetweenRangeType(sourceRange: ICheckpointRange, targetRange: ICheckpointRange): boolean {
        const sourceRangeStart: number = isDefined(sourceRange.MinValue)
            ? sourceRange.MinValue
            : Number.MIN_SAFE_INTEGER;
        const sourceRangeEnd: number = isDefined(sourceRange.MaxValue) ? sourceRange.MaxValue : Number.MAX_SAFE_INTEGER;
        const targetRangeStart: number = isDefined(targetRange.MinValue)
            ? targetRange.MinValue
            : Number.MIN_SAFE_INTEGER;
        const targetRangeEnd: number = isDefined(targetRange.MaxValue) ? targetRange.MaxValue : Number.MAX_SAFE_INTEGER;

        return sourceRangeEnd >= targetRangeStart && sourceRangeStart <= targetRangeEnd;
    }

    private isDuplicateRangeWithSameCheckpointStatus(
        sourceRange: ICheckpointRange,
        targetRange: ICheckpointRange,
    ): boolean {
        // https://chainformation.atlassian.net/browse/FI-5795
        //  If a user enters multiple similar value ranges, display an error message and highlight the corresponding field.
        return (
            targetRange.MaxValue === sourceRange.MaxValue &&
            targetRange.MinValue === sourceRange.MinValue &&
            targetRange.Type === sourceRange.Type &&
            isDefined(sourceRange.CheckpointStatus) &&
            isDefined(targetRange.CheckpointStatus) &&
            targetRange.CheckpointStatus === sourceRange.CheckpointStatus
        );
    }

    private isMinGreaterThanMaxRangeValue(): boolean {
        return (
            this.checkpointRange.Type === CheckpointRangeType.Between &&
            this.checkpointRange.MinValue >= this.checkpointRange.MaxValue
        );
    }

    private isLessGreaterRangeOverlap(sourceRange: ICheckpointRange, targetRange: ICheckpointRange): boolean {
        return (
            sourceRange.Type === CheckpointRangeType.Greater &&
            targetRange.Type === CheckpointRangeType.Less &&
            sourceRange.MinValue <= targetRange.MaxValue
        );
    }

    private onlyNotOkRanges(): boolean {
        return (
            this.allCheckpointRanges.length &&
            this.allCheckpointRanges.every((checkpointRange: ICheckpointRange) => {
                return checkpointRange.CheckpointStatus === Checkpoint2StatusType.NotOk;
            })
        );
    }

    getMeasureUnitsText(): string {
        return this.fiCheckpoint2HelperService.getCheckpointMeasureUnitShortText(this.measureUnitType);
    }

    protected readonly CheckpointRangeType = CheckpointRangeType;
    protected readonly validateOnlyNumberKeyboardEvent = validateOnlyNumberKeyboardEvent;

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
}
